import { Component } from '@theme/component';

const URL_PATTERN = /^(https?:\/\/)?([a-z0-9äöüß-]+\.)+[a-z]{2,}(:\d+)?([/?#]\S*)?$/i;
const UID_PATTERN = /^[A-Z]{2}[A-Z0-9]{2,13}$/;

/**
 * Order form of the "24 Creatives" buy box (sections/c24-offer.liquid). Without this module the
 * form still posts to /cart/add with return_to=/checkout. With it: live validation with inline
 * errors, a readiness state for the checkout button, the UID field only for company purchases,
 * and a fetch-based add that replaces an earlier line of the same product before going to checkout.
 *
 * @typedef {object} OrderRefs
 * @property {HTMLDetailsElement} details - Wrapper that unfolds the form
 * @property {HTMLFormElement} form - The order form
 * @property {HTMLButtonElement} submit - Checkout button
 * @property {HTMLElement} submitLabel - Label inside the checkout button
 * @property {HTMLElement} missing - Hint above the button with the number of open answers
 * @property {HTMLElement} status - Live region for request errors
 * @property {HTMLInputElement} company - Company purchase checkbox
 * @property {HTMLInputElement} uid - UID number input
 * @property {HTMLDetailsElement} [second] - Optional second offer
 * @property {HTMLInputElement} [secondInput] - Link of the second offer
 *
 * @extends {Component<OrderRefs>}
 */
class C24Order extends Component {
  requiredRefs = ['details', 'form', 'submit', 'submitLabel', 'missing', 'status', 'company', 'uid'];

  /** @type {AbortController | undefined} */
  #request;
  /** @type {Set<HTMLInputElement>} */
  #touched = new Set();
  /** @type {string} */
  #submitText = '';

  connectedCallback() {
    super.connectedCallback();
    const { form, submitLabel } = this.refs;
    // Validation messages are rendered inline by this component.
    form.noValidate = true;
    this.#submitText = submitLabel.textContent || '';
    this.#syncCompany();
    this.#syncSecond();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
  }

  /** Moves focus into the form once the call to action has unfolded it. */
  onToggle() {
    if (!this.refs.details.open) return;
    const first = this.refs.form.querySelector('input[type="text"]');
    if (first instanceof HTMLInputElement) {
      requestAnimationFrame(() => first.focus({ preventScroll: true }));
    }
  }

  /** Enables the second offer only while it is unfolded, so a closed one never ends up in the order. */
  onSecondToggle() {
    this.#syncSecond();
    if (this.refs.second?.open) {
      requestAnimationFrame(() => this.refs.secondInput?.focus());
    }
    this.#update();
  }

  /** @param {Event} event */
  onInput(event) {
    const input = fieldOf(event);
    if (input instanceof HTMLInputElement && this.#touched.has(input)) this.#validateField(input);
    this.refs.status.textContent = '';
    this.#update();
  }

  /** @param {Event} event */
  onChange(event) {
    const input = fieldOf(event);
    if (input === this.refs.company) this.#syncCompany();
    if (input instanceof HTMLInputElement && input.type === 'text') {
      this.#touched.add(input);
      this.#validateField(input);
    }
    this.#update();
  }

  /** @param {SubmitEvent} event */
  async onSubmit(event) {
    event.preventDefault();
    const { form, submit, status } = this.refs;
    if (submit.getAttribute('aria-busy') === 'true') return;

    const fields = this.#fields();
    for (const field of fields) this.#touched.add(field);
    const invalid = fields.filter((field) => !this.#validateField(field));
    this.#update();
    if (invalid.length) {
      invalid[0]?.focus();
      return;
    }

    for (const field of fields) {
      if (field.hasAttribute('data-url') && field.value.trim()) field.value = normalizeUrl(field.value);
      if (field.hasAttribute('data-uid')) field.value = field.value.replace(/\s+/g, '').toUpperCase();
    }

    this.#setBusy(true);
    status.textContent = '';
    this.#request?.abort();
    this.#request = new AbortController();
    const { signal } = this.#request;

    const variantId = Number(this.dataset.variantId);
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [name, value] of new FormData(form)) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (match?.[1] && typeof value === 'string' && value.trim()) properties[match[1]] = value.trim();
    }

    try {
      // One order of this offer at a time: drop an earlier line of the same variant first.
      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { signal, headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`cart ${cartResponse.status}`);
      const cart = await cartResponse.json();
      for (const item of cart.items || []) {
        if (item.variant_id !== variantId) continue;
        const removed = await fetch(`${this.dataset.cartChangeUrl}.js`, {
          method: 'POST',
          signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
        if (!removed.ok) throw new Error(`change ${removed.status}`);
      }

      const added = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] }),
      });
      if (!added.ok) throw new Error(`add ${added.status}`);

      window.location.assign(this.dataset.checkoutUrl || '/checkout');
    } catch (error) {
      if (signal.aborted) return;
      console.error(error);
      this.#setBusy(false);
      status.textContent = this.dataset.errorRequest || '';
      // Fall back to the plain form post, which also lands in checkout.
      form.submit();
    }
  }

  /** @returns {HTMLInputElement[]} Text inputs that currently take part in the order */
  #fields() {
    return [...this.refs.form.querySelectorAll('input[type="text"]')].filter(
      (input) => input instanceof HTMLInputElement && !input.disabled
    );
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string} Error message, empty when valid
   */
  #errorFor(input) {
    const value = input.value.trim();
    if (!value) return input.required ? this.dataset.errorRequired || '' : '';
    if (input.hasAttribute('data-url') && !URL_PATTERN.test(value)) return this.dataset.errorUrl || '';
    if (input.hasAttribute('data-uid') && !UID_PATTERN.test(value.replace(/\s+/g, '').toUpperCase())) {
      return this.dataset.errorUid || '';
    }
    return '';
  }

  /**
   * Shows or clears the inline error of one field.
   * @param {HTMLInputElement} input
   * @returns {boolean} Whether the field is valid
   */
  #validateField(input) {
    const message = this.#errorFor(input);
    const errorId = (input.getAttribute('aria-describedby') || '').split(' ').find((id) => id.includes('Error'));
    const error = errorId ? document.getElementById(errorId) : null;
    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
    if (message) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
    input.classList.toggle('is-valid', !message && !!input.value.trim());
    return !message;
  }

  /** Updates the readiness state and the hint about open answers. */
  #update() {
    const open = this.#fields().filter((field) => this.#errorFor(field)).length;
    const { form, missing } = this.refs;
    const ready = open === 0;
    if (form.dataset.ready !== String(ready)) form.dataset.ready = String(ready);
    if (ready) {
      missing.textContent = '';
    } else if (open === 1) {
      missing.textContent = this.dataset.missingOne || '';
    } else {
      missing.textContent = (this.dataset.missingMany || '').replace('[count]', String(open));
    }
  }

  #syncCompany() {
    const { company, uid } = this.refs;
    uid.required = company.checked;
    uid.disabled = !company.checked;
    if (!company.checked) {
      this.#touched.delete(uid);
      this.#validateField(uid);
    }
  }

  #syncSecond() {
    const { second, secondInput } = this.refs;
    if (!second || !secondInput) return;
    secondInput.disabled = !second.open;
    if (!second.open) {
      this.#touched.delete(secondInput);
      this.#validateField(secondInput);
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const { submit, submitLabel } = this.refs;
    submit.setAttribute('aria-busy', String(busy));
    submitLabel.textContent = busy ? this.dataset.busy || this.#submitText : this.#submitText;
  }
}

/**
 * The framework routes form events with `target` set to the form, so read the field from the path.
 * @param {Event} event
 * @returns {EventTarget | null}
 */
function fieldOf(event) {
  return event.composedPath()[0] ?? event.target;
}

/**
 * Adds https:// to links typed without a scheme.
 * @param {string} value
 */
function normalizeUrl(value) {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

if (!customElements.get('c24-order')) customElements.define('c24-order', C24Order);
